import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import PhishingOutlinedIcon from "@mui/icons-material/PhishingOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useLang } from "../i18n/LangContext";
import { useAppState } from "../state/AppState";
import { loadPosts } from "../content/blog";
import { SEOHead, SITE_NAME, SITE_URL } from "../components/SEOHead";

export function HomePage() {
  const { t, lang } = useLang();
  const { spots } = useAppState();
  const posts = loadPosts().slice(0, 3);
  const freeSpots = spots.filter((s) => s.isFree).length;
  const sourceCount = new Set(spots.map((s) => s.source?.name).filter(Boolean)).size;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: lang,
    description: t("homeHeroLead"),
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/map?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <>
      <SEOHead
        title={t("homeHeroTitle")}
        description={t("homeHeroLead")}
        path="/"
        jsonLd={jsonLd}
      />
      <main className="page page--home">
        <section className="hero">
          <div className="hero__decor" aria-hidden="true">
            <svg
              className="hero__mountains"
              viewBox="0 0 1200 240"
              preserveAspectRatio="xMidYMax slice"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="m1" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#0e1a2d" stopOpacity="0" />
                  <stop offset="100%" stopColor="#0e1a2d" stopOpacity="0.95" />
                </linearGradient>
              </defs>
              <path
                d="M0 240 L0 170 L120 110 L210 150 L290 80 L380 140 L470 100 L560 145 L640 60 L740 140 L830 95 L930 150 L1030 90 L1120 155 L1200 120 L1200 240 Z"
                fill="url(#m1)"
              />
              <path
                d="M0 240 L0 200 L80 170 L160 195 L240 160 L340 200 L430 170 L530 205 L620 175 L720 210 L810 180 L900 205 L1000 175 L1100 210 L1200 180 L1200 240 Z"
                fill="#0a1322"
                opacity="0.85"
              />
            </svg>
          </div>
          <Box className="hero__inner">
            <Typography
              variant="overline"
              sx={{
                color: "primary.main",
                letterSpacing: "0.18em",
                fontWeight: 700,
              }}
            >
              🇮🇸 ÍSLAND · VEIÐIÁR
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.08,
                fontSize: { xs: 30, sm: 42, md: 54 },
                mt: 1,
              }}
            >
              {t("homeHeroTitle")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mt: 2.5,
                color: "text.secondary",
                fontSize: { xs: 16, sm: 18 },
                maxWidth: 640,
                lineHeight: 1.55,
              }}
            >
              {t("homeHeroLead")}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ mt: 3 }}
            >
              <Button
                component={RouterLink}
                to="/map"
                variant="contained"
                size="large"
                startIcon={<MapOutlinedIcon />}
              >
                {t("homeCtaMap")}
              </Button>
              <Button
                component={RouterLink}
                to="/blog"
                variant="outlined"
                size="large"
              >
                {t("homeCtaBlog")}
              </Button>
            </Stack>
            {spots.length > 0 && (
              <div className="hero__stats" aria-label="stats">
                <div className="hero-stat">
                  <span className="hero-stat__num">{spots.length}</span>
                  <span className="hero-stat__lbl">{t("homeStatsSpots")}</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat__num">{sourceCount || 6}</span>
                  <span className="hero-stat__lbl">{t("homeStatsSources")}</span>
                </div>
                {freeSpots > 0 && (
                  <div className="hero-stat">
                    <span className="hero-stat__num">{freeSpots}</span>
                    <span className="hero-stat__lbl">{t("homeStatsFree")}</span>
                  </div>
                )}
              </div>
            )}
          </Box>
        </section>

        <section className="page-section motivation">
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 700, letterSpacing: "-0.01em", mb: 2 }}
          >
            {t("homeMotivationTitle")}
          </Typography>
          <div className="motivation__body">
            <p>{t("homeMotivationBody1")}</p>
            <p>{t("homeMotivationBody2")}</p>
            <p>{t("homeMotivationBody3")}</p>
          </div>
        </section>

        <section className="page-section">
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 700, letterSpacing: "-0.01em", mb: 2.5 }}
          >
            {t("homeGettingStartedTitle")}
          </Typography>
          <ol className="steps">
            <li className="step">
              <span className="step__num">1</span>
              <div>
                <h3 className="step__title">{t("homeGettingStartedStep1Title")}</h3>
                <p className="step__body">{t("homeGettingStartedStep1Body")}</p>
              </div>
            </li>
            <li className="step">
              <span className="step__num">2</span>
              <div>
                <h3 className="step__title">{t("homeGettingStartedStep2Title")}</h3>
                <p className="step__body">{t("homeGettingStartedStep2Body")}</p>
              </div>
            </li>
            <li className="step">
              <span className="step__num">3</span>
              <div>
                <h3 className="step__title">{t("homeGettingStartedStep3Title")}</h3>
                <p className="step__body">{t("homeGettingStartedStep3Body")}</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="page-section">
          <div className="feature-grid">
            <FeatureCard
              icon={<BoltOutlinedIcon />}
              title={t("homeFeatureLiveTitle")}
              body={t("homeFeatureLiveBody")}
            />
            <FeatureCard
              icon={<PhishingOutlinedIcon />}
              title={t("homeFeatureSpeciesTitle")}
              body={t("homeFeatureSpeciesBody")}
            />
            <FeatureCard
              icon={<VolunteerActivismOutlinedIcon />}
              title={t("homeFeatureFreeTitle")}
              body={t("homeFeatureFreeBody")}
            />
          </div>
        </section>

        {posts.length > 0 && (
          <section className="page-section">
            <div className="section-head">
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                {t("homeBlogTeaserTitle")}
              </Typography>
              <Button
                component={RouterLink}
                to="/blog"
                endIcon={<ArrowForwardIcon />}
                size="small"
              >
                {t("homeBlogTeaserMore")}
              </Button>
            </div>
            <div className="blog-grid">
              {posts.map((p) => (
                <Card key={p.slug} variant="outlined">
                  <CardActionArea component={RouterLink} to={`/blog/${p.slug}`}>
                    <CardContent>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {p.date} · {p.readingTime} {t("blogReadingTime")}
                      </Typography>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ mt: 0.5, fontWeight: 700 }}
                      >
                        {p.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, color: "text.secondary" }}
                      >
                        {p.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </div>
          </section>
        )}

        <section className="page-section feedback-cta">
          <div className="feedback-cta__inner">
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              {t("homeFeedbackTitle")}
            </Typography>
            <Typography sx={{ mt: 1, color: "text.secondary", maxWidth: 560 }}>
              {t("homeFeedbackBody")}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<EmailOutlinedIcon />}
              href="mailto:hi@fishing-iceland.app?subject=Feedback%20Fishing%20Iceland"
              sx={{ mt: 2.5 }}
            >
              {t("feedbackLink")}
            </Button>
          </div>
        </section>
      </main>
    </>
  );
}

function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Card variant="outlined" className="feature-card">
      <CardContent>
        <div className="feature-card__icon" aria-hidden="true">
          {icon}
        </div>
        <Typography variant="h6" component="h3" sx={{ mt: 1.5, fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          {body}
        </Typography>
      </CardContent>
    </Card>
  );
}
